import { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../../config";
import useJobs from "../../hooks/useJobs";
import useActiveJobId from "../../hooks/useActiveJobId";
import useJobWaiter from "../../hooks/useJobWaiter";
import ComplianceBrief from "./ComplianceBrief";
import DataTable from "./DataTable";
import DocumentPicker from "./DocumentPicker";
import "./CompliancePipeline.css";

/**
 * Document-agnostic compliance extraction. For ANY ingested document it derives
 * the document's OWN schema (sections + columns) from its structure, lets the user
 * review it, then extracts each section into generic column-keyed rows. Works on
 * newly-uploaded documents automatically — no per-document or per-type code.
 */
export default function DiscoveryView({ database, initialDocId = "" }) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | discovering | discovered | extracting | done
  const [error, setError] = useState(null);
  const [schema, setSchema] = useState(null);
  const [selected, setSelected] = useState(null); // Set of section keys to extract
  const [brief, setBrief] = useState(null);
  const [notice, setNotice] = useState("");
  const { startJob } = useJobs({ enabled: false }); // only need startJob; no polling here
  const { waitFor } = useJobWaiter();
  // Persists the running job so we can re-attach after navigating away and back.
  const [activeJob, setActiveJob, clearActiveJob] = useActiveJobId("approvalDiscoverActiveJob");
  // Cancels an in-flight schema poll on unmount / when a newer discovery starts.
  const discoverTokenRef = useRef(null);
  useEffect(() => () => { discoverTokenRef.current = null; }, []);

  // Wait for a background job to complete via the shared jobs SSE feed, guarded by
  // the shared discover token so a stale wait never writes state after unmount or
  // after a newer run started.
  const pollJob = useCallback((jobId, token, { onDone, onFailed }) => {
    waitFor(jobId)
      .then((job) => {
        if (discoverTokenRef.current !== token) return; // cancelled
        if (job.status === "failed") onFailed(job.error || "unbekannt");
        else onDone(job);
      })
      .catch((err) => {
        if (discoverTokenRef.current === token) onFailed(err.message);
      });
  }, [waitFor]);

  const sectionKeys = schema?.sections ? schema.sections.map((s) => s.key) : [];
  const selCount = selected ? selected.size : 0;
  const allSelected = sectionKeys.length > 0 && selCount === sectionKeys.length;
  const toggleSel = (key) =>
    setSelected((prev) => {
      const next = new Set(prev || sectionKeys);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  const setAllSelected = (on) => setSelected(on ? new Set(sectionKeys) : new Set());


  // Refresh the document list (new uploads appear here automatically).
  useEffect(() => {
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/approval/z-documents${qs}`)
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((d) => setDocs(d.documents || []))
      .catch(() => setDocs([]));
  }, [database]);

  const applySchema = useCallback((data) => {
    setSchema(data);
    setSelected(new Set((data?.sections || []).map((s) => s.key))); // default: all
    setNotice("");
    setPhase("discovered");
  }, []);

  const discover = useCallback(async (force = false) => {
    if (!docId) return;
    setPhase("discovering"); setError(null); setNotice(""); setSchema(null); setBrief(null);
    // Token guards against a stale poll writing state after unmount or a newer run.
    const token = {};
    discoverTokenRef.current = token;
    const fail = (msg) => {
      if (discoverTokenRef.current !== token) return;
      setNotice(""); setError(`Schema discovery failed: ${msg}`); setPhase("idle");
    };
    try {
      const data = await startJob("/api/v1/approval/discover/schema", {
        document_id: docId, database: database || "Betonbau", force,
      });
      if (!data.job_id) {            // synchronous fallback (jobs disabled)
        if (discoverTokenRef.current === token) applySchema(data.result);
        return;
      }
      setActiveJob({ jobId: data.job_id, step: "schema", docId });
      setNotice("Schema-Analyse läuft im Hintergrund…");
      pollJob(data.job_id, token, {
        onDone: (job) => { clearActiveJob(); applySchema(job.result); },
        onFailed: (msg) => { clearActiveJob(); fail(msg); },
      });
    } catch (e) { fail(e.message); }
  }, [docId, database, startJob, applySchema, pollJob, setActiveJob, clearActiveJob]);

  // Deep-link: when opened from a tray job, preselect that document and auto-run
  // discovery once (schema is cached, so it returns quickly).
  const autoDocRef = useRef(null);
  useEffect(() => {
    if (initialDocId) { setDocId(initialDocId); autoDocRef.current = initialDocId; }
  }, [initialDocId]);
  useEffect(() => {
    if (autoDocRef.current && docId === autoDocRef.current) {
      autoDocRef.current = null;
      discover(false);
    }
  }, [docId, discover]);

  // Re-attach on mount to a job that was still running when we navigated away.
  const reattachedRef = useRef(false);
  useEffect(() => {
    if (reattachedRef.current || !activeJob?.jobId) return;
    reattachedRef.current = true;
    autoDocRef.current = null; // reattach supersedes the deep-link auto-discover
    const { jobId, step, docId: savedDoc } = activeJob;
    const token = {};
    discoverTokenRef.current = token;
    if (savedDoc) setDocId(savedDoc);
    if (step === "extract") {
      setPhase("extracting");
      setNotice("Extraktion läuft im Hintergrund…");
      pollJob(jobId, token, {
        onDone: (job) => { clearActiveJob(); setBrief(job.result); setNotice(""); setPhase("done"); },
        onFailed: (msg) => { clearActiveJob(); setError(`Extraction failed: ${msg}`); setPhase("idle"); },
      });
    } else {
      setPhase("discovering");
      setNotice("Schema-Analyse läuft im Hintergrund…");
      pollJob(jobId, token, {
        onDone: (job) => { clearActiveJob(); applySchema(job.result); },
        onFailed: (msg) => { clearActiveJob(); setError(`Schema discovery failed: ${msg}`); setPhase("idle"); },
      });
    }
  }, [activeJob, pollJob, applySchema, clearActiveJob]);

  const updatePages = (idx, value) => {
    const pages = value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    setSchema((s) => ({ ...s, sections: s.sections.map((sec, i) => (i === idx ? { ...sec, pages } : sec)) }));
  };

  const extract = useCallback(async (force = false) => {
    const all = schema?.sections || [];
    const sel = selected || new Set(all.map((s) => s.key));
    const chosen = all.filter((s) => sel.has(s.key));
    if (chosen.length === 0) return;
    // A subset can't reuse the (whole-document) cached brief, so always re-extract.
    const isSubset = chosen.length < all.length;
    const sendSchema = { ...schema, sections: chosen };
    setPhase("extracting"); setError(null); setNotice(""); setBrief(null);
    const token = {};
    discoverTokenRef.current = token;
    try {
      const data = await startJob("/api/v1/approval/discover/run", {
        document_id: docId, database: database || "Betonbau",
        schema: sendSchema, force: force || isSubset,
      });
      if (!data.job_id) {            // synchronous fallback (jobs disabled)
        if (discoverTokenRef.current === token) { setBrief(data.result); setPhase("done"); }
        return;
      }
      setActiveJob({ jobId: data.job_id, step: "extract", docId });
      setNotice("Extraktion läuft im Hintergrund — das Ergebnis erscheint hier, sobald es fertig ist.");
      pollJob(data.job_id, token, {
        onDone: (job) => { clearActiveJob(); setBrief(job.result); setNotice(""); setPhase("done"); },
        onFailed: (msg) => {
          clearActiveJob();
          if (discoverTokenRef.current === token) { setError(`Extraction failed: ${msg}`); setPhase("discovered"); }
        },
      });
    } catch (e) { setError(`Extraction failed: ${e.message}`); setPhase("discovered"); }
  }, [docId, database, schema, selected, startJob, pollJob, setActiveJob, clearActiveJob]);

  const revise = useCallback(async () => {
    if (!docId) return;
    setPhase("extracting"); setError(null);
    const token = {};
    discoverTokenRef.current = token;
    try {
      const data = await startJob("/api/v1/approval/discover/revision", {
        document_id: docId, database: database || "Betonbau",
      });
      if (!data.job_id) {            // synchronous fallback (jobs disabled)
        if (discoverTokenRef.current === token) { setBrief(data.result); setPhase("done"); }
        return;
      }
      pollJob(data.job_id, token, {
        onDone: (job) => { setBrief(job.result); setPhase("done"); },
        onFailed: (msg) => { setError(`Re-detection failed: ${msg}`); setPhase("done"); },
      });
    } catch (e) { setError(`Re-detection failed: ${e.message}`); setPhase("done"); }
  }, [docId, database, startJob, pollJob]);

  const selectDocument = (id) => { setDocId(id); setSchema(null); setSelected(null); setBrief(null); setPhase("idle"); };

  return (
    <div className="cp-root">
      <div className="ds-toolbar cp-controls">
        <DocumentPicker
          id="dv-doc"
          label="Document"
          placeholder="— select any ingested document —"
          documents={docs}
          value={docId}
          onChange={selectDocument}
        />
        <button className="ds-btn ds-btn--primary" onClick={() => discover(false)} disabled={!docId || phase === "discovering" || phase === "extracting"}>
          {phase === "discovering" ? "Discovering schema…" : "1 · Discover schema"}
        </button>
        {schema && phase !== "discovering" && (
          <button className="ds-btn ds-btn--secondary" onClick={() => discover(true)} title="Ignore cache and re-derive">
            ↻ Re-discover
          </button>
        )}
      </div>
      {schema && phase === "discovered" && (
        <p className="ds-help cp-hint">
          Schema is cached — re-opening this document is instant. Use ↻ Re-discover after re-ingest.
        </p>
      )}

      {error && <div className="ds-alert ds-alert--danger ds-flush">{error}</div>}
      {notice && <div className="ds-alert ds-flush ds-alert--info">{notice}</div>}

      {/* Step 1: discovered schema (editable pages) */}
      {schema && (
        <div className="ds-card cp-card">
          <div className="ds-section-title cp-card-head">Discovered schema
            <span className="ds-badge">{schema.document_type}</span>
            <span className="ds-meta ds-push-end">
              {selCount}/{sectionKeys.length} ausgewählt
            </span>
          </div>
          <DataTable>
            <thead><tr>
              <th className="cp-col-check">
                <input type="checkbox" checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = selCount > 0 && !allSelected; }}
                  onChange={(e) => setAllSelected(e.target.checked)}
                  title="Alle aus-/abwählen" aria-label="Alle aus-/abwählen" />
              </th>
              <th>Section</th><th>Pages</th><th>Columns (from the document)</th>
            </tr></thead>
            <tbody>
              {schema.sections.map((s, i) => {
                const on = selected ? selected.has(s.key) : true;
                return (
                  <tr key={s.key} className={on ? undefined : "cp-row-off"}>
                    <td><input type="checkbox" checked={on} onChange={() => toggleSel(s.key)}
                      aria-label={`Extract ${s.label}`} /></td>
                    <td><b className="cp-topic">{s.label}</b><div className="cp-src">{s.key}</div></td>
                    <td className="cp-col-pages">
                      <input className="ds-input ds-input--sm cp-pages" defaultValue={(s.pages || []).join(", ")}
                        aria-label={`Pages for ${s.label}`}
                        onBlur={(e) => updatePages(i, e.target.value)} />
                    </td>
                    <td className="cp-src">{(s.columns || []).join(" · ") || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
          <div className="cp-card-actions">
            <button className="ds-btn ds-btn--primary" onClick={() => extract(false)}
              disabled={phase === "extracting" || selCount === 0}>
              {phase === "extracting"
                ? "Extracting…"
                : allSelected ? "2 · Alle Abschnitte extrahieren" : `2 · Auswahl extrahieren (${selCount})`}
            </button>
            {brief && phase === "done" && (
              <button className="ds-btn ds-btn--secondary" onClick={() => extract(true)} title="Ignore cache and re-extract">
                ↻ Re-extract
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 2: extracted brief — rendered by the shared ComplianceBrief so the
          live view and the saved (read-only) view stay identical. */}
      {brief && (
        <ComplianceBrief
          brief={brief}
          documentId={docId}
          database={database || "Betonbau"}
          onRevise={revise}
        />
      )}
    </div>
  );
}
